import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Plus } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { PRODUCTS, ART_WALL_SETS, ART_WALL_PRICE, COLORS, getProduct, eur } from "@/lib/shop";

const NAMES = {
  "mur-noir": { en: "Le Mur Noir — three Noir", fr: "Le Mur Noir — trois Noir" },
  "colour-wall": { en: "The Colour Wall — three Bi-color", fr: "Le Mur Couleur — trois Bi-color" },
  "le-salon": { en: "Le Salon — mixed", fr: "Le Salon — mixte" },
};

// Live preview: three prints hanging on a cream wall.
function WallPreview({ picks }) {
  return (
    <div
      className="relative border gallery-line bg-[#efe9df] aspect-[16/9] w-full overflow-hidden"
      data-testid="aw-preview"
    >
      <div className="absolute inset-x-[8%] top-[16%] flex items-start justify-center gap-[6%]">
        {[0, 1, 2].map((i) => {
          const pick = picks[i];
          const product = pick ? getProduct(pick.product) : null;
          return (
            <div
              key={i}
              className="w-[27%] border border-[#111111]/50 bg-white shadow-none"
              data-testid={`aw-slot-${i}`}
            >
              {product ? (
                <img
                  src={product.prints[pick.color]}
                  alt={product.name}
                  className="aspect-[4/5] w-full object-contain p-2"
                />
              ) : (
                <div className="aspect-[4/5] w-full grid place-items-center">
                  <Plus className="h-6 w-6 text-[#111111]/25" strokeWidth={1} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* sofa line */}
      <div className="absolute bottom-0 inset-x-[12%] h-[22%] border-t-2 border-[#111111]/15 bg-[#e3d9c8]/60" />
    </div>
  );
}

export default function ArtWalls() {
  const { addItem } = useCart();
  const { t, lang } = useLanguage();
  const [picks, setPicks] = useState([null, null, null]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [added, setAdded] = useState(false);

  const chosen = picks.filter(Boolean).length;
  const complete = chosen === 3;

  const addWall = (wallPicks, label) => {
    addItem({
      product_id: "art-wall",
      product_name: `Art Wall — ${label}`,
      color: "mix",
      size: "40x50",
      framing: "unframed",
      bundle_items: wallPicks.map((p) => ({
        product_id: p.product,
        product_name: getProduct(p.product)?.name || p.product,
        color: p.color,
      })),
      quantity: 1,
      unit_price: ART_WALL_PRICE,
      image: getProduct(wallPicks[0].product)?.prints[wallPicks[0].color] || null,
    });
    setAdded(true);
    toast.success(t("product.added"));
    setTimeout(() => setAdded(false), 2000);
  };

  const setsValid = useMemo(() => ART_WALL_SETS, []);

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">{t("aw.kicker")}</span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]">
            {t("aw.title.a")}
            <span className="italic">{t("aw.title.i")}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#111111]/70">
            {t("aw.intro")}
          </p>

          {/* Curated walls */}
          <div className="mt-16">
            <span className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">01 — {t("aw.curated")}</span>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 border-t border-l gallery-line">
              {setsValid.map((s) => (
                <div key={s.id} className="border-r border-b gallery-line" data-testid={`aw-set-${s.id}`}>
                  <div className="overflow-hidden bg-white">
                    <img src={s.image} alt={NAMES[s.id][lang] || NAMES[s.id].en} loading="lazy"
                      className="aspect-[4/3] w-full object-cover" />
                  </div>
                  <div className="px-6 py-6">
                    <h3 className="font-display italic text-xl tracking-tight text-[#111111]">
                      {NAMES[s.id][lang] || NAMES[s.id].en}
                    </h3>
                    <p className="mt-2 text-xs font-light text-[#111111]/60">
                      {s.picks.map((p) => `${getProduct(p.product)?.name} (${p.color === "noir" ? "Noir" : "Bi-color"})`).join(" · ")}
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-light text-[#111111]">
                        {eur(ART_WALL_PRICE, lang)}{" "}
                        <span className="text-xs text-[#111111]/50 line-through ml-1">
                          {eur(225, lang)}
                        </span>
                      </span>
                      <button
                        onClick={() => addWall(s.picks, NAMES[s.id].en)}
                        data-testid={`aw-add-${s.id}`}
                        className="border border-[#111111] px-5 py-2.5 text-xs font-light tracking-wide hover:bg-[#111111] hover:text-[#faf8f5] transition-colors duration-300"
                      >
                        {t("aw.addWall")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Build your own */}
          <div className="mt-24">
            <span className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">02 — {t("aw.build")}</span>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <Reveal className="lg:col-span-7">
                <WallPreview picks={picks} />
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm font-light text-[#111111]/70">
                    {complete ? (
                      <span className="font-display italic">{t("aw.saving")}</span>
                    ) : (
                      t("aw.pickMore").replace("{n}", String(3 - chosen))
                    )}
                  </p>
                  <button
                    onClick={() => complete && addWall(picks, "Build your own")}
                    disabled={!complete}
                    data-testid="aw-add-custom"
                    className="bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85 disabled:opacity-40"
                  >
                    {added ? <Check className="h-4 w-4" strokeWidth={1.5} /> : t("aw.addWall")}
                  </button>
                </div>
              </Reveal>

              <div className="lg:col-span-5">
                {/* slot selector */}
                <div className="flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <button
                      key={i}
                      onClick={() => setActiveSlot(i)}
                      data-testid={`aw-slot-btn-${i}`}
                      className={`flex-1 border px-4 py-2.5 text-xs font-light transition-colors duration-300 ${
                        activeSlot === i ? "border-[#111111] bg-[#f1ede7]" : "border-[#111111]/20"
                      }`}
                    >
                      {t("aw.pick")} {i + 1}
                      {picks[i] ? ` — ${getProduct(picks[i].product)?.name.replace("The ", "")}` : ""}
                    </button>
                  ))}
                </div>
                {/* product picker */}
                <div className="mt-4 max-h-[420px] overflow-y-auto border gallery-line bg-white">
                  {PRODUCTS.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 border-b border-[#111111]/8 px-4 py-3 last:border-b-0">
                      <img src={p.prints[picks[activeSlot]?.color || "noir"]} alt={p.name}
                        className="h-14 w-11 object-contain bg-white" loading="lazy" />
                      <span className="flex-1 font-display italic text-sm text-[#111111]">{p.name}</span>
                      {COLORS.map((c) => (
                        <button
                          key={c.id}
                          data-testid={`aw-pick-${activeSlot}-${p.id}-${c.id}`}
                          onClick={() => {
                            const next = [...picks];
                            next[activeSlot] = { product: p.id, color: c.id };
                            setPicks(next);
                            if (activeSlot < 2) setActiveSlot(activeSlot + 1);
                          }}
                          className={`border px-3 py-1.5 text-[11px] font-light transition-colors duration-200 ${
                            picks[activeSlot]?.product === p.id && picks[activeSlot]?.color === c.id
                              ? "border-[#111111] bg-[#111111] text-[#faf8f5]"
                              : "border-[#111111]/25 text-[#111111]/70 hover:border-[#111111]"
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
